<script>
    // 허브 [기본] 탭 — 학습자에게 **무엇이 보이는가**를 한 화면에 모은다.
    //   로고(하단 푸터) · 캐릭터(격자 위) · 소개 문구(캐릭터 위) · 단계별 멘트(캐릭터 아래)
    //
    // 왜 따로 탭인가: 이 넷은 허브 화면의 내용물이다. 예전엔 셋으로 흩어져 있었다 —
    // 멘트·캐릭터는 [디자인], 소개 문구는 [설정], 로고는 [디자인]의 공용 섹션.
    // 저작자가 한 화면을 만드는 데 탭을 오가야 했다.
    // 남은 [디자인] 탭은 배경·버튼·폰트, 즉 **어떻게 보일까**만 갖는다(2D·기업형 공용).
    //
    // ⚠️ 저장은 setHubChats 가 아니라 **setHubSettings** 다. 전자는 격자를 통째로 교체하는
    //    저장이라(delete → 재삽입) chats 없이 부르면 **격자가 전부 지워진다.**
    import { onDestroy, onMount } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import { Section, Input, Textarea } from "../../lib/ui";
    // 이미지 칸은 빌더 공통 카드(ImageUploadCard)를 쓴다 — 바로 위 '로고'와 같은 모양이어야 한다.
    // pickMode = 로컬 업로드 대신 **이미지 자산 라이브러리**에서 고르는 모드(챗 빌더 ProfileContext 와 동일).
    import ImageUploadCard from "../chat-builder/ImageUploadCard.svelte";
    import { markSaving, markSaved } from "../store/builder-store.js";
    import { messaging } from "../store/messaging-store";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi";

    export let bot;

    let subTitle = "";
    let logoAssetId = null;
    let logoUrl = "";
    let logoPickerOpen = false;
    let msgIntake = "";
    let msgMain = "";
    let msgExit = "";
    let characterAssetId = null;
    // 미리보기 URL. 저장하는 값은 ref(assetId) 뿐이고, 이 URL 은
    //   · 최초 로드 → 서버가 current 버전으로 resolve 해 준 값(getHubChats)
    //   · 고른 직후 → ImagePicker 가 함께 준 값
    // 두 경로 모두 '지금 current' 라서 저작 화면과 학습자 화면이 같은 이미지를 본다.
    let characterUrl = "";
    let libraryOpen = false;
    // 타이틀 이미지(메인 화면 캐릭터 위 제목 그림) — 캐릭터와 같은 규칙: 저장은 ref, 화면은 current URL
    let titleAssetId = null;
    let titleUrl = "";
    let titlePickerOpen = false;
    // 타이틀 그림 움직임(BOT_HUB_SETTINGS.TITLE_MOTION) — null=없음(기본). 허용 값은 서버 util/hubCardStyle HUB_TITLE_MOTIONS,
    // 모양은 PROFILE hub.css 와 이 파일 맨 아래 스타일(저작 화면 미리보기)이 같은 값(3px · 2° · 3%, 3초)으로 그린다.
    let titleMotion = null;
    const TITLE_MOTIONS = [
        { key: null, label: "움직임 없음" },
        { key: "float", label: "둥실 (위아래)" },
        { key: "sway", label: "살랑 (좌우 기울기)" },
        { key: "breathe", label: "숨쉬기 (크기)" },
    ];
    // 레벨 아이콘 — 사전 테스트 구간(🍃·🌸·🍎)마다 허브 화면에 보여줄 그림. [{label, from, to, assetId, iconUrl}]
    // ⚠️ 구간 라벨은 바꾸지 않는다(레슨 챗 대화 지시가 이 글자를 키로 쓴다) — 허브 화면에서만 그림으로 바꿔 보여준다.
    let levels = [];
    let levelsTouched = false;   // 손댔을 때만 저장한다 — 사전 테스트를 잠시 빼 둔 사이 다른 칸 저장이 아이콘을 지우지 않게
    let levelPickFor = null;     // 고르는 창이 어느 구간 것인지
    let levelPickerOpen = false;
    let hasExitChat = false;    // 사후 챗이 격자에 있나 — 없으면 사후 멘트가 뜰 일이 없다
    let loading = true;
    let error = "";

    // 빌더 관례대로 자동저장(상단 '저장 중…/저장됨'이 진행 표시). 사용자 조작에서만 예약한다 —
    // 반응형으로 감시하면 최초 로드에서도 한 번 저장된다.
    let saveTimer;
    const SAVE_DEBOUNCE_MS = 1000;

    // 읽기는 격자와 한 곳(getHubChats)에서 온다 — 허브 한 장의 저작값이 거기 다 실려 있다.
    // 쓰기만 갈라져 있다(격자=setHubChats / 화면=setHubSettings).
    onMount(() => {
        PRConnect.send("/api/HubSetup/getHubChats", { botId: bot.id }, (data) => {
            const r = data.result || {};
            msgIntake = r.msgIntake || "";
            msgMain = r.msgMain || "";
            msgExit = r.msgExit || "";
            characterAssetId = r.characterAssetId || null;
            characterUrl = r.characterImageUrl || "";
            titleAssetId = r.titleAssetId || null;
            titleUrl = r.titleImageUrl || "";
            titleMotion = r.titleMotion || null;
            levels = r.levels || [];
            subTitle = r.subTitle || "";
            logoAssetId = r.logoAssetId || null;
            logoUrl = r.logoImageUrl || "";
            hasExitChat = (r.chats || []).some((c) => c.role === "exit");
            loading = false;
        }, (err) => {
            error = err.header?.message || "불러오지 못했습니다.";
            loading = false;
        });
    });

    // ── 단계별 멘트 ↔ 미리보기 ── 멘트 칸을 누르면 미리보기가 그 단계 화면(사전 = [시작하기] 버튼 / 메인 / 사후)으로 바뀐다.
    // 학습자 상태(로그인·사전 통과·진도)를 만들지 않고도 각 화면을 확인하라는 것 — 미리보기에서만 통한다(PROFILE usePreviewStage).
    // 이 탭을 떠나면(컴포넌트가 내려가면) 원래 화면으로 되돌린다 — 다른 탭에서까지 사전 화면이 남아 있으면 헷갈린다.
    let previewStage = null;
    const showStage = (stage) => {
        if (previewStage === stage) return;
        previewStage = stage;
        messaging.set({ target: "Preview", action: "hubPreviewStage", stage });
    };
    onDestroy(() => showStage(null));

    // 허브 화면 전부 — 미리보기를 이 화면으로 돌린다(런처 빌더 [화면] 탭과 같은 생각: 만드는 걸 보면서 만든다).
    // 로그인·회원가입은 학습자로 로그인하지 않은 저작자에게 원래 보이지만, 사전·메인·사후는 학습자 상태를 만들어야만 보였다.
    const SCREENS = [
        { key: null, label: "실제 화면" },
        { key: "login", label: "로그인" },
        { key: "signup", label: "회원가입" },
        { key: "intake", label: "사전 테스트" },
        { key: "main", label: "메인" },
        { key: "exit", label: "사후 테스트" },
    ];

    const scheduleSave = () => {
        clearTimeout(saveTimer);
        saveTimer = setTimeout(save, SAVE_DEBOUNCE_MS);
    };

    const save = () => {
        error = "";
        // Bot 인스턴스의 소개 문구도 맞춰 둔다 — BOTS.SUB_TITLE 을 같이 쓰는 저장(이름 바꾸기·[설정] 탭)이
        // 빌더를 연 시점 값으로 이걸 되돌리지 않게.
        bot.subTitle = subTitle;
        markSaving();
        PRConnect.send("/api/HubSetup/setHubSettings", {
            botId: bot.id, msgIntake, msgMain, msgExit, characterAssetId, titleAssetId, titleMotion, subTitle, logoAssetId,
            ...(levelsTouched ? { levelIcons: levels.map((l) => ({ label: l.label, assetId: l.assetId })) } : {}),
        }, () => markSaved(), (err) => {
            markSaved();     // pill 이 'saving' 에 갇히지 않게 되돌린다
            error = err.header?.message || "저장하지 못했습니다.";
        });
    };

    // 고른 자산은 assetId(ref)로 저장한다. url 은 미리보기 전용 —
    // url 을 저장하면 자산을 교체해도 허브가 옛 이미지를 계속 가리킨다.
    const onCharacterPicked = (e) => {
        characterAssetId = e.detail?.assetId || null;
        characterUrl = e.detail?.url || "";
        libraryOpen = false;
        scheduleSave();
    };

    const onLogoPicked = (e) => {
        logoAssetId = e.detail?.assetId || null;
        logoUrl = e.detail?.url || "";
        logoPickerOpen = false;
        scheduleSave();
    };

    // ⚠️ '지우기'가 로고를 반드시 없애는 게 아니다 — 자산 ref 만 지우고, 옛 업로드 로고가
    //    BOT_DESIGN 에 남아 있으면 서버가 그쪽으로 폴백한다(듀얼 모드).
    const clearLogo = () => {
        logoAssetId = null;
        logoUrl = "";
        scheduleSave();
    };

    const clearCharacter = () => {
        characterAssetId = null;
        characterUrl = "";
        scheduleSave();
    };

    const onTitlePicked = (e) => {
        titleAssetId = e.detail?.assetId || null;
        titleUrl = e.detail?.url || "";
        titlePickerOpen = false;
        scheduleSave();
    };
    const pickLevelIcon = (label) => {
        levelPickFor = label;
        levelPickerOpen = true;
    };
    const setLevelIcon = (label, assetId, url) => {
        levels = levels.map((l) => (l.label === label ? { ...l, assetId, iconUrl: url } : l));
        levelsTouched = true;
        scheduleSave();
    };
    const onLevelIconPicked = (e) => {
        levelPickerOpen = false;
        if (levelPickFor) setLevelIcon(levelPickFor, e.detail?.assetId || null, e.detail?.url || null);
    };

    const setTitleMotion = (key) => {
        if (titleMotion === key) return;
        titleMotion = key;
        scheduleSave();
    };

    const clearTitle = () => {
        titleAssetId = null;
        titleUrl = "";
        scheduleSave();
    };

    // 허브 이름(BOTS.TITLE) — 콘솔 목록·[허브관리자] 탭의 허브 고르기에 보이는 이름. 허브가 여럿이면 여기서 구분한다.
    // 기존 이름 바꾸기(bot.changeBotTitle)를 그대로 쓴다 — 이미지 자산 폴더(봇/{이름})도 함께 옮겨야 해서.
    // ⚠️ 그 저장(modifyBot)은 소개 문구(SUB_TITLE)도 같이 보낸다. Bot 인스턴스 값은 빌더를 연 시점 값이라
    //    여기서 고친 최신 소개 문구로 맞춘 뒤 보낸다 — 안 그러면 방금 고친 소개 문구가 옛 값으로 돌아간다.
    const renameHub = (e) => {
        const name = String(e.target.value || "").trim();
        if (!name) {
            e.target.value = bot.title || "";   // 빈 이름은 받지 않는다 — 목록에서 허브를 못 찾게 된다
            return;
        }
        if (name === bot.title) return;
        bot.subTitle = subTitle;
        bot.changeBotTitle(name);
    };

    onDestroy(() => clearTimeout(saveTimer));
</script>

{#if !loading}
    <Section title="화면 미리보기" description="누르면 오른쪽 미리보기가 그 화면으로 바뀝니다. '실제 화면'은 지금 로그인 상태 그대로입니다.">
        <div class="tw-flex tw-flex-wrap tw-gap-2">
            {#each SCREENS as sc (sc.label)}
                <button type="button"
                        class="tw-h-8 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-xs tw-font-medium tw-transition-colors {previewStage === sc.key
                            ? 'tw-border-primary tw-bg-primary tw-text-primary-foreground'
                            : 'tw-border-border tw-bg-background tw-text-foreground hover:tw-bg-accent'}"
                        aria-pressed={previewStage === sc.key}
                        on:click={() => showStage(sc.key)}>
                    {sc.label}
                </button>
            {/each}
        </div>
    </Section>

    <Section title="허브 이름" description="콘솔 목록과 [허브관리자] 탭에서 이 허브를 가리키는 이름입니다. 허브가 여러 개면 서로 구분되게 지어 주세요.">
        <Input type="text" maxlength="256"
               placeholder="예) 용산글로벌센터 — 영어 말하기"
               value={bot.title ?? ""}
               on:change={renameHub} />
    </Section>

    <Section title="로고" description="페이지 맨 아래에 들어갑니다. 없으면 아바톡 로고가 나옵니다.">
        <div class="tw-max-w-[260px]">
            <ImageUploadCard
                pickMode
                imageUrl={logoUrl || null}
                emptyText="클릭해서 이미지 자산에서 고르세요"
                hint="배경이 투명한 png 권장"
                minHeight="150px"
                on:pick={() => (logoPickerOpen = true)}
                on:remove={clearLogo}
            />
        </div>
    </Section>

    <Section title="소개 문구" description="허브 맨 위, 캐릭터 그림 위에 뜨는 한 줄입니다. 비우면 아무것도 안 나옵니다.">
        <Input type="text" maxlength="256"
               placeholder="예) 매일 10분, 영어가 입에 붙습니다"
               value={subTitle}
               on:input={(e) => { subTitle = e.target.value; scheduleSave(); }} />
    </Section>

    <Section title="타이틀 이미지" description="메인 화면 맨 위 가운데에 뜨는 제목 그림입니다(예: '용이랑 영어스쿨'). 맨 아래 로고와는 다른 그림이에요.">
        <!-- 고른 움직임을 이 미리보기 그림에도 그대로 건다(맨 아래 스타일) — 허브 미리보기는 배포된 화면 번들이 있어야 움직인다 -->
        <div class="tw-max-w-[260px] title-motion-preview" data-motion={titleUrl && titleMotion ? titleMotion : null}>
            <ImageUploadCard
                pickMode
                imageUrl={titleUrl || null}
                emptyText="클릭해서 이미지 자산에서 고르세요"
                hint="배경이 투명한 png 권장 · 가로로 긴 그림"
                minHeight="120px"
                on:pick={() => (titlePickerOpen = true)}
                on:remove={clearTitle}
            />
        </div>
        <div class="tw-mt-3 tw-space-y-1.5">
            <span class="tw-block tw-text-xs tw-font-medium tw-text-muted-foreground">움직임</span>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
                {#each TITLE_MOTIONS as m (m.label)}
                    <button type="button"
                            class="tw-h-8 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-xs tw-font-medium tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-50 {titleMotion === m.key
                                ? 'tw-border-primary tw-bg-primary tw-text-primary-foreground'
                                : 'tw-border-border tw-bg-background tw-text-foreground hover:tw-bg-accent'}"
                            aria-pressed={titleMotion === m.key}
                            disabled={!titleUrl}
                            on:click={() => setTitleMotion(m.key)}>
                        {m.label}
                    </button>
                {/each}
            </div>
            <span class="tw-block tw-text-xs tw-text-muted-foreground">
                {titleUrl ? "타이틀 그림이 살짝 계속 움직입니다. 기기에서 '동작 줄이기'를 켠 학습자에게는 멈춰 보여요." : "타이틀 이미지를 고르면 움직임을 정할 수 있어요."}
            </span>
        </div>
    </Section>

    <Section title="캐릭터" description="격자 위에 크게 뜨는 그림입니다.">
        <div class="tw-max-w-[260px]">
            <ImageUploadCard
                pickMode
                imageUrl={characterUrl || null}
                emptyText="클릭해서 이미지 자산에서 고르세요"
                hint="배경이 투명한 png 권장"
                minHeight="150px"
                on:pick={() => (libraryOpen = true)}
                on:remove={clearCharacter}
            />
        </div>
    </Section>

    <Section title="레벨 아이콘" description="메인 화면 왼쪽 위 레벨 알약에 보여줄 그림입니다. 사전 테스트의 구간(이모지)은 그대로 두고, 허브 화면에서만 이 그림으로 바꿔 보여줘요.">
        {#if levels.length === 0}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">
                [채팅] 탭에서 사전 테스트를 담고, 그 채팅의 판정 설정에 구간을 만들면 여기 나타납니다.
            </p>
        {:else}
            <div class="tw-space-y-2">
                {#each levels as l (l.label)}
                    <div class="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2">
                        <span class="tw-w-8 tw-shrink-0 tw-text-center tw-text-xl" title="사전 테스트 구간 라벨">{l.label}</span>
                        <span class="tw-w-14 tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-muted-foreground">레벨 {l.from}–{l.to}</span>
                        <button type="button"
                                class="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-solid tw-border-border tw-bg-white hover:tw-ring-2 hover:tw-ring-ring"
                                title="이미지 자산에서 고르기" aria-label="{l.label} 구간 아이콘 고르기" on:click={() => pickLevelIcon(l.label)}>
                            {#if l.iconUrl}
                                <img class="tw-h-7 tw-w-7 tw-object-contain" src={l.iconUrl} alt="" loading="lazy" />
                            {:else}
                                <span class="tw-text-xs tw-text-muted-foreground">＋</span>
                            {/if}
                        </button>
                        <span class="tw-min-w-0 tw-flex-1 tw-text-xs tw-text-muted-foreground">
                            {l.iconUrl ? "이 그림으로 보여요" : "비우면 이모지 그대로"}
                        </span>
                        {#if l.iconUrl}
                            <button type="button" class="tw-shrink-0 tw-text-xs tw-text-muted-foreground tw-underline hover:tw-text-foreground"
                                    on:click={() => setLevelIcon(l.label, null, null)}>지우기</button>
                        {/if}
                    </div>
                {/each}
            </div>
        {/if}
    </Section>

    <Section title="단계별 멘트" description="캐릭터 아래에 뜨는 말입니다. 학습자가 어디에 있느냐에 따라 셋 중 하나가 보입니다. 비워 두면 아무것도 안 나옵니다.">
        <div class="tw-space-y-3">
            <p class="tw-mb-0 tw-text-xs tw-text-muted-foreground">멘트 칸을 누르면 오른쪽 미리보기가 그 단계 화면으로 바뀝니다.</p>
            <label class="tw-mb-0 tw-block tw-space-y-1 tw-font-normal">
                <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
                    사전 — 사전 테스트를 아직 안 마친 학습자에게
                    {#if previewStage === "intake"}<span class="tw-rounded-full tw-bg-primary/10 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-text-primary">미리보기 중</span>{/if}
                </span>
                <Textarea rows="2" maxlength="500"
                          placeholder="예) 먼저 간단히 실력을 확인할게요."
                          value={msgIntake}
                          on:focus={() => showStage("intake")}
                          on:input={(e) => { msgIntake = e.target.value; scheduleSave(); }} />
            </label>
            <label class="tw-mb-0 tw-block tw-space-y-1 tw-font-normal">
                <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
                    메인 — 평소 화면
                    {#if previewStage === "main"}<span class="tw-rounded-full tw-bg-primary/10 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-text-primary">미리보기 중</span>{/if}
                </span>
                <Textarea rows="2" maxlength="500"
                          placeholder="예) 오늘은 어떤 걸 해볼까요?"
                          value={msgMain}
                          on:focus={() => showStage("main")}
                          on:input={(e) => { msgMain = e.target.value; scheduleSave(); }} />
            </label>
            <label class="tw-mb-0 tw-block tw-space-y-1 tw-font-normal">
                <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
                    사후 — 메인을 다 끝냈거나 운영자가 사후를 연 학습자에게
                    {#if previewStage === "exit"}<span class="tw-rounded-full tw-bg-primary/10 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-text-primary">미리보기 중</span>{/if}
                </span>
                <Textarea rows="2" maxlength="500"
                          placeholder="예) 마무리 점검을 해볼까요?"
                          value={msgExit}
                          on:focus={() => showStage("exit")}
                          on:input={(e) => { msgExit = e.target.value; scheduleSave(); }} />
            </label>
            {#if msgExit && !hasExitChat}
                <!-- 사후 챗이 격자에 없으면 사후 단계 자체가 열리지 않는다(갈 곳 없는 단계로 보내지 않는다).
                     문구만 써 두고 "왜 안 뜨지" 하는 걸 막는다. -->
                <p class="tw-mb-0 tw-rounded-md tw-bg-muted tw-px-3 tw-py-2 tw-text-xs tw-text-muted-foreground">
                    사후 <b>채팅이 격자에 없어</b> 이 멘트는 아직 뜨지 않습니다.
                    [채팅] 탭의 <b>사후 테스트</b> 자리에서 [사전 테스트로 만들기]를 누르거나, 용도가 사후 테스트인 채팅을 담아 주세요.
                </p>
            {/if}
            {#if error}<p class="tw-mb-0 tw-text-sm tw-text-destructive">{error}</p>{/if}
        </div>
    </Section>
{/if}

<ImagePicker
    bind:open={logoPickerOpen}
    mode="single"
    contextFolder={botAssetFolder(bot)}
    currentAssetId={logoAssetId}
    on:select={onLogoPicked}
/>

<ImagePicker
    bind:open={libraryOpen}
    mode="single"
    contextFolder={botAssetFolder(bot)}
    currentAssetId={characterAssetId}
    on:select={onCharacterPicked}
/>

<ImagePicker
    bind:open={titlePickerOpen}
    mode="single"
    contextFolder={botAssetFolder(bot)}
    currentAssetId={titleAssetId}
    on:select={onTitlePicked}
/>

<ImagePicker
    bind:open={levelPickerOpen}
    mode="single"
    contextFolder={botAssetFolder(bot)}
    currentAssetId={levels.find((l) => l.label === levelPickFor)?.assetId ?? null}
    on:select={onLevelIconPicked}
/>

<style>
    /* 타이틀 그림 움직임 미리보기 — PROFILE hub.css(.hub-title-image[data-motion])와 같은 값. 바꾸면 둘 다 바꿀 것.
       ImageUploadCard 안의 그림이라 :global(img) 로 건다. keyframes 는 이름이 겹치지 않게 -global- 로 둔다. */
    .title-motion-preview[data-motion] :global(img) {
        animation-duration: 3s;
        animation-timing-function: ease-in-out;
        animation-iteration-count: infinite;
    }
    .title-motion-preview[data-motion="float"] :global(img) { animation-name: hub-basic-title-float; }
    .title-motion-preview[data-motion="sway"] :global(img) { animation-name: hub-basic-title-sway; transform-origin: 50% 60%; }
    .title-motion-preview[data-motion="breathe"] :global(img) { animation-name: hub-basic-title-breathe; }
    @keyframes -global-hub-basic-title-float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
    }
    @keyframes -global-hub-basic-title-sway {
        0%, 100% { transform: rotate(-2deg); }
        50% { transform: rotate(2deg); }
    }
    @keyframes -global-hub-basic-title-breathe {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.03); }
    }
</style>
